import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { AppState } from '../../../../../../core/reducers';
import { AffiliationRelationService } from '../services';
import {
  CancelAffiliationRelationPage,
  ClearAffiliationRelationError,
  CreateAffiliationRelation,
  CreateAffiliationRelationOnServer,
  DeleteAffiliationRelation,
  DeleteAffiliationRelationOnServer,
  LoadAffiliationRelationPage,
  AffiliationRelationActionTypes,
  RequestAffiliationRelationPage,
  ThrowAffiliationRelationError,
  ToggleAffiliationRelationActionLoading,
  ToggleAffiliationRelationPageLoading,
  UpdateAffiliationRelation,
  UpdateAffiliationRelationOnServer,
} from '../actions';

@Injectable()
export class AffiliationRelationEffects {
  showPageLoading = new ToggleAffiliationRelationPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleAffiliationRelationPageLoading({ pageLoading: false });
  showActionLoading = new ToggleAffiliationRelationActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleAffiliationRelationActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private affiliationService: AffiliationRelationService,
  ) {}

  @Effect()
  loadAffiliationRelationPage$ = this.actions$.pipe(
    ofType<RequestAffiliationRelationPage>(
      AffiliationRelationActionTypes.RequestAffiliationRelationPage,
    ),
    map((action: RequestAffiliationRelationPage) => action.payload),
    mergeMap(({ entityId, entityType, query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.affiliationService.getAffiliationRelationPage(
        entityId,
        entityType,
        query,
      );
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearAffiliationRelationError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadAffiliationRelationPage({
              affiliationRelations: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelAffiliationRelationPage());

          return new ThrowAffiliationRelationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createAffiliationRelation$ = this.actions$.pipe(
    ofType<CreateAffiliationRelationOnServer>(
      AffiliationRelationActionTypes.CreateAffiliationRelationOnServer,
    ),
    map((action: CreateAffiliationRelationOnServer) => action.payload),
    mergeMap(({ affiliationRelation }) => {
      this.store.dispatch(this.showActionLoading);

      return this.affiliationService.createAffiliationRelation(affiliationRelation).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearAffiliationRelationError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateAffiliationRelation({ affiliationRelation: res.body });
          }

          return new ThrowAffiliationRelationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateAffiliationRelation$ = this.actions$.pipe(
    ofType<UpdateAffiliationRelationOnServer>(
      AffiliationRelationActionTypes.UpdateAffiliationRelationOnServer,
    ),
    map((action: UpdateAffiliationRelationOnServer) => action.payload),
    mergeMap(({ affiliationRelation }) => {
      this.store.dispatch(this.showActionLoading);

      return this.affiliationService.updateAffiliationRelation(affiliationRelation).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearAffiliationRelationError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateAffiliationRelation({ affiliationRelation: res.body });
          }

          return new ThrowAffiliationRelationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  deleteAffiliationRelation$ = this.actions$.pipe(
    ofType<DeleteAffiliationRelationOnServer>(
      AffiliationRelationActionTypes.DeleteAffiliationRelationOnServer,
    ),
    map((action: DeleteAffiliationRelationOnServer) => action.payload),
    mergeMap(({ id }) => {
      this.store.dispatch(this.showActionLoading);

      return this.affiliationService.deleteAffiliationRelation(id).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearAffiliationRelationError());
            this.store.dispatch(this.hideActionLoading);

            return new DeleteAffiliationRelation({ id });
          }

          return new ThrowAffiliationRelationError({ error: res });
        }),
      );
    }),
  );
}
